import React, { useState, useEffect } from "react";
import { db, ref, onValue, set } from "./firebase";
import { HOLIDAY_PRESETS, FIREBASE_HOLIDAY_PATH } from "./constants/holidayPresets";
import { Sparkles } from "lucide-react";

const AdminHolidayGreeting = () => {
  const [settings, setSettings] = useState({ enabled: false, holidayId: "ramadan" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const settingsRef = ref(db, FIREBASE_HOLIDAY_PATH);
    const unsubscribe = onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setSettings(snapshot.val());
      } else {
        setSettings({ enabled: false, holidayId: HOLIDAY_PRESETS[0].id });
      }
      setLoading(false);
    }, (err) => {
      console.error(err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await set(ref(db, FIREBASE_HOLIDAY_PATH), {
        enabled: settings.enabled,
        holidayId: settings.holidayId,
      });
      setMessage("Holiday greeting saved. Students will see it when they open the dashboard.");
    } catch (err) {
      console.error(err);
      setMessage("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const selectedPreset = HOLIDAY_PRESETS.find((p) => p.id === settings.holidayId) || HOLIDAY_PRESETS[0];

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-amber-100 text-amber-700">
          <Sparkles size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Holiday Greeting</h1>
          <p className="text-slate-500 text-sm">Choose a greeting and confetti that students see on the dashboard.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings((s) => ({ ...s, enabled: e.target.checked }))}
              className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium text-slate-800">Show holiday greeting to students</span>
          </label>
          <p className="text-sm text-slate-500 mt-2 ml-8">When enabled, students see the selected greeting once when they open the dashboard.</p>
        </div>

        <div className="p-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Select holiday</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {HOLIDAY_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setSettings((s) => ({ ...s, holidayId: preset.id }))}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                  settings.holidayId === preset.id
                    ? "border-blue-500 bg-blue-50 text-blue-900"
                    : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
                }`}
              >
                <span className="text-2xl">{preset.fallbackEmoji}</span>
                <span className="font-medium">{preset.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          <p className="text-sm text-slate-500 mb-3">Preview: students will see &quot;{selectedPreset.label}&quot; with confetti in your theme colors.</p>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
          >
            {saving ? "Saving..." : "Save settings"}
          </button>
          {message && (
            <p className={`mt-3 text-sm ${message.startsWith("Failed") ? "text-red-600" : "text-green-600"}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminHolidayGreeting;
